import { Navigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useFiscalStore } from "@/store/fiscal-store";
import { fmtBRL, fmtNum, fmtDur, fmtDate } from "@/lib/format";
import { exportDashboardPdf } from "@/lib/export-pdf";
import { Download, FileText, Table2, Clock, CheckCircle2, Loader2, Filter, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import type { NotaStatus } from "@/lib/fiscal-types";

interface ExportLog {
  id: string;
  tipo: "PDF" | "Excel";
  timestamp: string;
  totalNotas: number;
  status: "success" | "error";
  label: string;
}

export default function ExportacoesPage() {
  const { dashboard, filteredNotas, divergencias, duracaoMs, setFiltroStatus } = useFiscalStore();
  const [exportando, setExportando] = useState<"pdf" | "excel" | null>(null);
  const [logs, setLogs] = useState<ExportLog[]>([]);
  const [filtroStatus, setFiltroStatusLocal] = useState<NotaStatus | "all">("all");
  const [filtroModelo, setFiltroModelo] = useState<"all" | "55" | "65">("all");
  const [tipoExportacao, setTipoExportacao] = useState<"basico" | "produtos" | "monofasico" | "fornecedor">("basico");
  const tickRef = useRef<number | null>(null);

  useEffect(() => { document.title = "Exportações — DataGrid XML"; }, []);
  useEffect(() => () => { if (tickRef.current) clearInterval(tickRef.current); }, []);

  // Removido redirecionamento automático para permitir acesso livre conforme solicitado.
  // if (!dashboard && filteredNotas.length === 0) return <Navigate to="/upload" replace />;

  const notasFiltradas = filteredNotas.filter((n) => {
    if (filtroStatus !== "all" && n.notaStatus !== filtroStatus) return false;
    if (filtroModelo !== "all" && String(n.modelo) !== filtroModelo) return false;
    return true;
  });

  const addLog = (log: ExportLog) => setLogs((prev) => [log, ...prev].slice(0, 20));

  const handlePDF = () => {
    if (exportando) return;
    setExportando("pdf");
    setTimeout(() => {
      try {
        exportDashboardPdf(dashboard, notasFiltradas, duracaoMs);
        toast.success("PDF exportado com sucesso.");
        addLog({ id: crypto.randomUUID(), tipo: "PDF", timestamp: new Date().toISOString(), totalNotas: notasFiltradas.length, status: "success", label: `Dashboard + ${fmtNum(notasFiltradas.length)} notas` });
      } catch (e: any) {
        toast.error("Falha ao gerar PDF: " + (e?.message ?? "erro desconhecido"));
        addLog({ id: crypto.randomUUID(), tipo: "PDF", timestamp: new Date().toISOString(), totalNotas: notasFiltradas.length, status: "error", label: "Erro na exportação" });
      } finally {
        setExportando(null);
      }
    }, 50);
  };

  const handleExcel = () => {
    if (exportando) return;
    setExportando("excel");
    setTimeout(() => {
      try {
        let headers: string[] = [];
        let rows: any[][] = [];

        if (tipoExportacao === "basico") {
          headers = [
            "Chave", "Modelo", "Número", "Série", "Emissão", "Emitente", "CNPJ Emit", "Destinatário", "CNPJ Dest", 
            "UF Emit", "UF Dest", "Nat.Op", "CFOP Principal", "Valor Total", "Valor Itens", "Descontos", "Frete",
            "vICMS", "vST", "vFCP", "vFCPST", "vIPI", "vPIS", "vCOFINS", "vIBS", "vCBS", "Status Sefaz", "Achados Malha"
          ];
          rows = notasFiltradas.map((n) => [
            n.chave, n.modelo, n.numero, n.serie,
            n.emissao?.slice(0, 10) ?? "",
            n.emitente, n.cnpjEmit, n.destinatario, n.cnpjDest,
            n.ufEmit, n.ufDest, n.natOp, n.cfopPrincipal,
            n.valorOficial.toFixed(2), n.valorItens.toFixed(2), n.vDesc.toFixed(2), n.vFrete.toFixed(2),
            n.vICMS.toFixed(2), n.vST.toFixed(2), n.vFCP.toFixed(2), n.vFCPST.toFixed(2), n.vIPI.toFixed(2), n.vPIS.toFixed(2), n.vCOFINS.toFixed(2),
            n.vIBS.toFixed(2), n.vCBS.toFixed(2), n.notaStatus, n.auditFindings?.length ?? 0
          ]);
        } else if (tipoExportacao === "fornecedor") {
          headers = [
            "CNPJ Emitente", "Fornecedor", "Qtd. Notas", "Números das Notas",
            "Total Movimentado (vNF)", "Total Itens", "Descontos", "Frete",
            "ICMS", "ICMS-ST", "FECP", "FECP-ST", "IPI", "PIS", "COFINS", "IBS", "CBS"
          ];
          
          const map = new Map<string, any>();
          notasFiltradas.forEach(n => {
            const cnpj = n.cnpjEmit || "ND";
            if (!map.has(cnpj)) {
              map.set(cnpj, { 
                cnpj, emitente: n.emitente || "Não Informado", 
                qtd: 0, numeros: [],
                vNF: 0, vItens: 0, vDesc: 0, vFrete: 0,
                icms: 0, st: 0, fcp: 0, fcpst: 0, ipi: 0, pis: 0, cofins: 0, ibs: 0, cbs: 0 
              });
            }
            const f = map.get(cnpj);
            f.qtd += 1;
            if (n.numero) f.numeros.push(n.numero);
            f.vNF += n.valorOficial || 0;
            f.vItens += n.valorItens || 0;
            f.vDesc += n.vDesc || 0;
            f.vFrete += n.vFrete || 0;
            f.icms += n.vICMS || 0;
            f.st += n.vST || 0;
            f.fcp += n.vFCP || 0;
            f.fcpst += n.vFCPST || 0;
            f.ipi += n.vIPI || 0;
            f.pis += n.vPIS || 0;
            f.cofins += n.vCOFINS || 0;
            f.ibs += n.vIBS || 0;
            f.cbs += n.vCBS || 0;
          });

          rows = Array.from(map.values()).map(f => [
            f.cnpj, f.emitente, f.qtd, f.numeros.join(", "),
            f.vNF.toFixed(2), f.vItens.toFixed(2), f.vDesc.toFixed(2), f.vFrete.toFixed(2),
            f.icms.toFixed(2), f.st.toFixed(2), f.fcp.toFixed(2), f.fcpst.toFixed(2), 
            f.ipi.toFixed(2), f.pis.toFixed(2), f.cofins.toFixed(2), f.ibs.toFixed(2), f.cbs.toFixed(2)
          ]);

        } else {
          // Relatório Detalhado de Produtos ou Monofásico
          headers = [
            "Chave NF-e", "Número NF-e", "Emitente", "CNPJ Emit", "Data Emissão",
            "Código Prod", "EAN", "Descrição do Produto", "NCM", "CEST", "CFOP",
            "Unidade", "Qtd", "Vlr Unit", "Vlr Total", 
            "vBC ICMS", "Aliq ICMS", "vICMS", "vBC ST", "Aliq ST", "vICMS ST",
            "CST PIS", "vBC PIS", "Aliq PIS", "vPIS",
            "CST COFINS", "vBC COFINS", "Aliq COFINS", "vCOFINS"
          ];

          notasFiltradas.forEach(n => {
            if (!n.itens) return;
            n.itens.forEach(item => {
              // Regra: se for monofásico, PIS ou COFINS deve ter CST 04 (Tributação Monofásica)
              const cstPis = item.impostos?.pis?.cst || "";
              const cstCofins = item.impostos?.cofins?.cst || "";
              
              if (tipoExportacao === "monofasico" && cstPis !== "04" && cstCofins !== "04") {
                return; // Pular itens que não são monofásicos
              }

              rows.push([
                n.chave, n.numero, n.emitente, n.cnpjEmit, n.emissao?.slice(0, 10) ?? "",
                item.cProd, (item as any).cEAN ?? "", item.xProd, item.ncm, (item as any).CEST ?? "", item.cfop,
                item.uCom, item.qCom, item.vUnCom.toFixed(4), item.vProd.toFixed(2),
                item.impostos?.icms?.vBC?.toFixed(2) || "0.00", item.impostos?.icms?.pICMS?.toFixed(2) || "0.00", item.impostos?.icms?.vICMS?.toFixed(2) || "0.00",
                item.impostos?.icmsST?.vBCST?.toFixed(2) || "0.00", item.impostos?.icmsST?.pICMSST?.toFixed(2) || "0.00", item.impostos?.icmsST?.vICMSST?.toFixed(2) || "0.00",
                cstPis, item.impostos?.pis?.vBC?.toFixed(2) || "0.00", item.impostos?.pis?.pPIS?.toFixed(2) || "0.00", item.impostos?.pis?.vPIS?.toFixed(2) || "0.00",
                cstCofins, item.impostos?.cofins?.vBC?.toFixed(2) || "0.00", item.impostos?.cofins?.pCOFINS?.toFixed(2) || "0.00", item.impostos?.cofins?.vCOFINS?.toFixed(2) || "0.00"
              ]);
            });
          });
        }

        const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Relatorio");
        XLSX.writeFile(wb, `${tipoExportacao}_${new Date().toISOString().slice(0, 10)}.xlsx`);
        
        toast.success(`Relatório ${tipoExportacao} exportado em Excel com sucesso.`);
        addLog({ id: crypto.randomUUID(), tipo: "Excel", timestamp: new Date().toISOString(), totalNotas: notasFiltradas.length, status: "success", label: `${fmtNum(rows.length)} registros (.xlsx)` });
      } catch (e: any) {
        toast.error("Falha ao gerar Excel: " + (e?.message ?? "erro"));
        addLog({ id: crypto.randomUUID(), tipo: "Excel", timestamp: new Date().toISOString(), totalNotas: 0, status: "error", label: "Erro na exportação" });
      } finally {
        setExportando(null);
      }
    }, 50);
  };

  

  return (
    <div className="px-4 py-8 md:px-8 max-w-[1600px] mx-auto space-y-10 animate-in fade-in slide-in-from-bottom-8 duration-700">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 glass-card p-6 border-l-4 border-l-primary">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-xl">
              <Download className="size-6 text-primary" />
            </div>
            <h1 className="text-2xl lg:text-4xl font-black tracking-tight text-foreground">Exportações</h1>
          </div>
          <p className="text-sm lg:text-base text-muted-foreground font-medium">
            Gere relatórios detalhados e planilhas de dados fiscais processados.
          </p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2.5 bg-primary/10 border border-primary/20 rounded-xl w-fit">
          <CheckCircle2 className="size-4 text-primary" />
          <span className="text-sm font-bold text-primary">Disponível: {fmtNum(filteredNotas.length)} documentos</span>
        </div>
      </header>

      {/* Painel de Filtros Glass */}
      <div className="glass-card p-6 lg:p-8 space-y-5 relative overflow-hidden group animate-in-card" style={{ animationDelay: '100ms' }}>
        <div className="absolute -right-10 -top-10 opacity-[0.02] group-hover:opacity-[0.04] transition-opacity"><Filter className="size-40" /></div>
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-8 relative z-10">
          <div className="space-y-5">
            <h2 className="text-xs font-black uppercase tracking-[0.2em] text-primary flex items-center gap-2">
              <Filter className="size-4" /> Critérios de Exportação
            </h2>
            <div className="flex flex-wrap gap-8">
              <div className="space-y-3">
                <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest block">Status da Nota</label>
                <div className="flex flex-wrap rounded-xl border border-border glass p-1 bg-background/30 shadow-inner">
                  {(["all", "AUTORIZADA", "CONTINGENCIA", "CANCELADA", "INUTILIZADA"] as const).map((s) => (
                    <button
                      key={s}
                      onClick={() => setFiltroStatusLocal(s)}
                      className={`px-4 py-2 text-[10px] font-bold rounded-lg transition-all duration-300 ${filtroStatus === s ? "bg-primary text-primary-foreground shadow-md" : "text-muted-foreground hover:text-foreground hover:bg-foreground/5"}`}
                    >
                      {s === "all" ? "Todos" : s === "CONTINGENCIA" ? "Contingência" : s === "INUTILIZADA" ? "Inutilizada" : s === "CANCELADA" ? "Cancelada" : "Autorizada"}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-3">
                <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest block">Modelo do Documento</label>
                <div className="flex flex-wrap rounded-xl border border-border glass p-1 bg-background/30 shadow-inner">
                  {(["all", "55", "65"] as const).map((m) => (
                    <button
                      key={m}
                      onClick={() => setFiltroModelo(m)}
                      className={`px-4 py-2 text-[10px] font-bold rounded-lg transition-all duration-300 ${filtroModelo === m ? "bg-primary text-primary-foreground shadow-md" : "text-muted-foreground hover:text-foreground hover:bg-foreground/5"}`}
                    >
                      {m === "all" ? "Todos" : m === "55" ? "NF-e (55)" : "NFC-e (65)"}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          <div className="xl:text-right space-y-2 bg-primary/5 p-6 rounded-2xl border border-primary/10 glass shadow-inner min-w-[260px]">
            <div className="text-[10px] font-black text-primary uppercase tracking-[0.2em]">Resumo da Seleção</div>
            <div className="text-3xl font-black text-foreground tabular-nums tracking-tighter">{fmtNum(notasFiltradas.length)} <span className="text-xs font-bold text-muted-foreground uppercase ml-1 tracking-widest">Notas</span></div>
            <div className="text-xl font-black text-emerald-500 tabular-nums">{fmtBRL(notasFiltradas.reduce((s, n) => s + n.valorOficial, 0))}</div>
          </div>
        </div>
      </div>

      {/* Botões de exportação Modernos */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="glass-card p-8 flex flex-col gap-5 relative overflow-hidden group border-red-500/20 animate-in-card hover-lift" style={{ animationDelay: '200ms' }}>
          <div className="absolute -right-8 -top-8 size-48 bg-red-500/10 blur-3xl rounded-full group-hover:bg-red-500/20 transition-all duration-700"></div>
          <div className="flex items-center gap-5 relative z-10">
            <div className="size-16 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center shadow-[0_0_30px_rgba(239,68,68,0.15)] group-hover:scale-110 transition-transform duration-500">
              <FileText className="size-8 text-red-500" />
            </div>
            <div>
              <h3 className="text-xl font-black tracking-tight text-foreground">Exportar Relatório PDF</h3>
              <p className="text-[10px] font-black text-red-500 uppercase tracking-widest mt-1 opacity-80">Dashboard + Documentos</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed relative z-10 min-h-[80px]">
            Gera um documento técnico formatado contendo os KPIs do dashboard, informações tributárias consolidadas e a listagem completa das notas filtradas. Ideal para conferências e apresentações internas.
          </p>
          <div className="mt-auto pt-2 relative z-10">
            <Button 
              onClick={handlePDF} 
              disabled={exportando !== null || notasFiltradas.length === 0}
              className="h-14 w-full rounded-xl gap-3 shadow-xl shadow-red-500/20 bg-red-600 hover:bg-red-500 text-white border-none font-bold text-sm transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:hover:scale-100"
            >
              {exportando === "pdf" ? <Loader2 className="size-5 animate-spin" /> : <Download className="size-5" />}
              {exportando === "pdf" ? "Gerando Documento..." : `Gerar PDF (${fmtNum(notasFiltradas.length)} notas)`}
            </Button>
          </div>
        </div>

        <div className="glass-card p-8 flex flex-col gap-5 relative overflow-hidden group border-emerald-500/20 animate-in-card hover-lift" style={{ animationDelay: '300ms' }}>
          <div className="absolute -right-8 -top-8 size-48 bg-emerald-500/10 blur-3xl rounded-full group-hover:bg-emerald-500/20 transition-all duration-700"></div>
          <div className="flex items-center gap-5 relative z-10">
            <div className="size-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shadow-[0_0_30px_rgba(16,185,129,0.15)] group-hover:scale-110 transition-transform duration-500">
              <Table2 className="size-8 text-emerald-500" />
            </div>
            <div>
              <h3 className="text-xl font-black tracking-tight text-foreground">Exportar Planilha Excel (.xlsx)</h3>
              <p className="text-[10px] font-black text-emerald-500 uppercase tracking-widest mt-1 opacity-80">Dados Tabulares Nativos</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed relative z-10 flex-1">
            Exporte tabelas dinâmicas em Excel nativo (.xlsx) contendo NCM, CFOP, Valores Unitários e detalhamento por Tributo (ICMS, PIS, COFINS).
          </p>
          
          <div className="space-y-3 relative z-10">
            <label className="text-[10px] font-black text-emerald-500 uppercase tracking-widest flex items-center gap-2">
              <Settings2 className="size-3" /> Tipo de Relatório (Granularidade)
            </label>
            <Select value={tipoExportacao} onValueChange={(v: any) => setTipoExportacao(v)}>
              <SelectTrigger className="input-premium h-12 border-emerald-500/20 focus:border-emerald-500/50 focus:ring-emerald-500/10 bg-background/50 text-foreground w-full">
                <SelectValue placeholder="Selecione o tipo de relatório" />
              </SelectTrigger>
              <SelectContent className="glass border-border">
                <SelectItem value="basico">Relatório Básico (Cabeçalhos das NFs)</SelectItem>
                <SelectItem value="produtos">Detalhamento de Produtos (Completo)</SelectItem>
                <SelectItem value="monofasico">Produtos com Tributação Monofásica (CST 04)</SelectItem>
                <SelectItem value="fornecedor">Totalização por Fornecedor (Agrupado)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="mt-auto pt-2 relative z-10">
            <Button 
              onClick={handleExcel} 
              disabled={exportando !== null || notasFiltradas.length === 0}
              className="h-14 w-full rounded-xl gap-3 shadow-xl shadow-emerald-500/20 bg-emerald-600 hover:bg-emerald-500 text-white border-none font-bold text-sm transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:hover:scale-100"
            >
              {exportando === "excel" ? <Loader2 className="size-5 animate-spin" /> : <Download className="size-5" />}
              {exportando === "excel" ? "Processando Planilha..." : `Baixar Excel (.xlsx) (${tipoExportacao === "basico" ? "Notas" : tipoExportacao === "fornecedor" ? "Fornecedores" : "Produtos"})`}
            </Button>
          </div>
        </div>
      </div>

      {/* Log de exportações Moderno */}
      {logs.length > 0 && (
        <div className="glass-card p-6 lg:p-8 space-y-6 animate-in-card" style={{ animationDelay: '400ms' }}>
          <h2 className="text-xs font-black uppercase tracking-[0.25em] text-primary flex items-center gap-3">
            <Clock className="size-4" /> Histórico de Exportações
          </h2>
          <div className="space-y-3">
            {logs.map((log) => (
              <div key={log.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-2xl border border-border bg-background/30 hover:bg-primary/5 hover:border-primary/20 transition-all group shadow-sm">
                <div className="flex items-center gap-4">
                  <div className={`size-12 rounded-xl flex items-center justify-center shrink-0 ${log.status === "success" ? "bg-emerald-500/10 text-emerald-500" : "bg-red-500/10 text-red-500"}`}>
                    {log.status === "success" ? <CheckCircle2 className="size-6" /> : <Download className="size-6" />}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-2">
                      <span className={`text-[9px] font-black px-2.5 py-0.5 rounded-full uppercase tracking-tighter ${log.tipo === "PDF" ? "bg-red-500/20 text-red-500" : "bg-emerald-500/20 text-emerald-500"}`}>
                        {log.tipo}
                      </span>
                      <span className="text-sm font-bold text-foreground truncate">{log.label}</span>
                    </div>
                  </div>
                </div>
                <div className="sm:text-right flex sm:flex-col gap-2 sm:gap-0.5 items-center sm:items-end text-muted-foreground">
                  <div className="text-[11px] font-black tabular-nums uppercase tracking-widest">{fmtDate(log.timestamp)}</div>
                  <div className="text-[10px] font-bold tabular-nums">{new Date(log.timestamp).toLocaleTimeString("pt-BR", { hour: '2-digit', minute: '2-digit' })}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
